// istanbul ignore file - low-level process callbacks

const exitHandlers: ExitHandler[] = [];

export type ExitHandler = () => void | Promise<void>;
const exitHandlerNames = new Set<string>(); // This is used to track global unique names for exit handlers

/**
 * We have a single global exit timer that is used to prevent multiple exit calls from
 * being made. This is useful for cases where we want to ensure that all exit handlers
 * are called, but we don't want to exit the process before we're ready.
 *
 * 🚩🚩🚩 You have 3 seconds to finish up and close everything before we hard stop
 *
 * This function does nothing in the browser environment.
 */
let globalExitTimer: NodeJS.Timeout | null = null;
let processHandlersAttached = false;

export function onExit(handler: ExitHandler) {
  if (typeof process === "undefined") {
    return;
  }
  if (exitHandlerNames.has(handler.name)) {
    throw new Error(
      `🚩  Exit handler ${handler.name} already registered. Please use a unique name for your exit handlers to make debugging easier.`
    );
  }
  exitHandlers.push(handler);
  if (processHandlersAttached) {
    return;
  }
  processHandlersAttached = true;
  process.on("SIGINT", exit);
  process.on("SIGTERM", exit);
}

export async function exit() {
  if (globalExitTimer || typeof process === "undefined") {
    return;
  }
  const runningExitHandlers = new Set<string>();
  globalExitTimer = setTimeout(() => {
    // eslint-disable-next-line no-console
    console.log(
      `🚩  Exiting with ${runningExitHandlers.size} running exit handlers... handler timeout exceeded`,
      `    ${Array.from(runningExitHandlers).join(", ")}`
    );
    process.exit(1);
  }, 3000);

  const work: (() => Promise<void>)[] = [];
  for (const handler of exitHandlers) {
    work.push(async () => {
      try {
        runningExitHandlers.add(handler.name);
        await handler();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`\n🚩  Exit handler failed: ${handler.name}\n`, e, "\n");
      } finally {
        runningExitHandlers.delete(handler.name);
      }
    });
  }

  await Promise.allSettled(work);
  // eslint-disable-next-line no-console
  console.log("\n👋  All exit handlers completed, exiting...");
  process.exit();
}
